import React, { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import type { PublicStackAnalysisPayload } from '../../../services/superPickerPublicApi';
import type { PublicUnitForMenu } from '../unit/types';
import { buildPublicUnitSyntheticKey } from '../unit/types';
import { useRevealedSection } from '../../../services/revealedPrices';
import { isUnitStatusNotReleased } from '../../../../utils/formatting';

interface StackAnalysisDialogProps {
  open: boolean;
  loading: boolean;
  data: PublicStackAnalysisPayload | null;
  projectId?: string | null;
  onClose: () => void;
  onUnitClick?: (unit: PublicUnitForMenu, clientX: number, clientY: number) => void;
}

const formatMaskedPrice = (value: string | null | undefined): string => {
  if (!value) return '-';
  return value.startsWith('$') ? value : `$${value}`;
};

const formatRevealedDollar = (value: number | null | undefined): string | null => {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  return `$${Math.round(value).toLocaleString('en-SG')}`;
};

const toMenuUnit = (
  unit: PublicStackAnalysisPayload['units'][number],
  projectId?: string | null,
): PublicUnitForMenu => ({
  unitKey: unit.unitKey,
  projectId,
  projectName: unit.projectName || '',
  unitNumber: unit.unitNumber,
  blockNumber: unit.blockNumber,
  bedroom: unit.bedroomNumber,
  bathroom: unit.bathroomNumber,
  areaSqft: unit.size,
  unitStatus: unit.status,
  floorplan: unit.floorplan,
  floorNumber: unit.floorNumber,
  priceDisplay: unit.priceDisplay ? formatMaskedPrice(unit.priceDisplay) : null,
  psfDisplay: unit.psfDisplay ? formatMaskedPrice(unit.psfDisplay) : null,
});

/**
 * Public stack-analysis dialog. Replicated from the agent
 * `SuperPickerApp.tsx` stack-analysis dialog without the floor price /
 * premium trend chart. All prices are masked server-side.
 */
export const StackAnalysisDialog: React.FC<StackAnalysisDialogProps> = ({
  open,
  loading,
  data,
  projectId,
  onClose,
  onUnitClick,
}) => {
  const revealedUnits = useRevealedSection('towerView', projectId ?? null);

  const resolveRevealed = (
    unit: PublicStackAnalysisPayload['units'][number],
  ) =>
    revealedUnits?.[unit.unitKey]
    ?? revealedUnits?.[buildPublicUnitSyntheticKey(unit.blockNumber, unit.unitNumber)];

  const unitsByFloor = useMemo(() => {
    if (!data?.units?.length) return [];
    const map = new Map<string, PublicStackAnalysisPayload['units']>();
    for (const unit of data.units) {
      const floor = unit.floorNumber || 'Unknown';
      const list = map.get(floor) ?? [];
      list.push(unit);
      map.set(floor, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      const fa = parseInt(a, 10) || 0;
      const fb = parseInt(b, 10) || 0;
      return fa - fb;
    });
  }, [data?.units]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          bgcolor: 'var(--color-surface)',
          color: 'rgba(var(--color-text-rgb), 0.9)',
          border: '1px solid rgba(var(--color-text-rgb), 0.08)',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: 'var(--color-text-strong)',
          fontWeight: 600,
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
          pb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: 'inherit' }}>
            Stack Analysis {data ? `- Stack ${data.stackNumber}` : ''}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 3,
          overflow: 'auto',
          maxHeight: '75vh',
          bgcolor: 'var(--color-surface)',
          borderColor: 'rgba(var(--color-text-rgb), 0.08)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={{ p: 1.5, bgcolor: 'rgba(var(--color-primary-rgb), 0.05)', border: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                      Stack Number
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {data.stackNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                      Total Units
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {data.totalUnits}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Paper sx={{ p: 2, bgcolor: 'var(--color-surface)', border: '1px solid rgba(var(--color-text-rgb), 0.08)', height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Availability Status
                    </Typography>
                    <Box sx={{ width: '100%', height: 220 }}>
                      {data.availableUnits + data.soldUnits === 0 ? (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                            No availability data
                          </Typography>
                        </Box>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                            <Pie
                              data={[
                                { name: 'Available', value: data.availableUnits, fill: '#4caf50' },
                                { name: 'Sold', value: data.soldUnits, fill: '#f44336' },
                              ]}
                              cx="50%"
                              cy="50%"
                              innerRadius={38}
                              outerRadius={62}
                              dataKey="value"
                              labelLine={false}
                              label={(entry: { name?: string; value?: number }) => {
                                const total = data.availableUnits + data.soldUnits;
                                const value = entry.value ?? 0;
                                const percent = total > 0 ? ((value / total) * 100).toFixed(0) : '0';
                                return `${entry.name ?? ''}: ${percent}%`;
                              }}
                              isAnimationActive={false}
                            >
                              {[
                                { fill: '#4caf50' },
                                { fill: '#f44336' },
                              ].map((entry, index) => (
                                <Cell key={`stack-pie-${index}`} fill={entry.fill} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 1 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#4caf50' }}>
                          {data.availableUnits}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                          Available
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#f44336' }}>
                          {data.soldUnits}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                          Sold
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Paper sx={{ p: 2, bgcolor: 'var(--color-surface)', border: '1px solid rgba(var(--color-text-rgb), 0.08)', height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Breakdown by Bedroom
                    </Typography>
                    <TableContainer sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 0.5 }}>Bedroom</TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>Available</TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>Sold</TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(data.bedroomBreakdown).map(([bedroom, row]) => (
                            <TableRow key={bedroom}>
                              <TableCell sx={{ py: 0.5 }}>
                                {bedroom === '6' ? 'Penthouse' : `${bedroom} BR`}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>{row.available}</TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>{row.sold}</TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>{row.available + row.sold}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Breakdown by Unit Type
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 0.5 }}>Unit Type</TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>Available</TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>Sold</TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(data.unitTypeBreakdown).map(([unitType, row]) => (
                            <TableRow key={unitType}>
                              <TableCell sx={{ py: 0.5 }}>{unitType}</TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>{row.available}</TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>{row.sold}</TableCell>
                              <TableCell align="right" sx={{ py: 0.5 }}>{row.available + row.sold}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'var(--color-surface)',
                  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                  position: 'sticky',
                  top: 0,
                  maxHeight: '80vh',
                  overflowY: 'auto',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Stack Tower View
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column-reverse', gap: 0.5 }}>
                  {unitsByFloor.map(([floor, floorUnits]) => (
                    <Box key={floor} sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5, mb: 0.5 }}>
                      <Box
                        sx={{
                          minWidth: 35,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                          borderRadius: 0.5,
                          px: 0.5,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.7)', fontSize: '0.7rem' }}>
                          {floor}F
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {floorUnits.map((unit) => {
                          const isSold = unit.status?.toLowerCase() === 'sold';
                          const isNotReleased = isUnitStatusNotReleased(unit.status);
                          const bedroomType = unit.bedroomNumber
                            ? (unit.bedroomNumber === 6 ? 'PH' : `${unit.bedroomNumber}BR`)
                            : '';
                          const clickable = Boolean(onUnitClick) && !isNotReleased;

                          return (
                            <Box
                              key={unit.unitKey}
                              onClick={(e) => {
                                if (!clickable || !onUnitClick) return;
                                onUnitClick(toMenuUnit(unit, projectId), e.clientX, e.clientY);
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                bgcolor: isSold ? 'rgba(244, 67, 54, 0.15)' : 'rgba(76, 175, 80, 0.15)',
                                border: `1px solid ${isSold ? '#f44336' : '#4caf50'}`,
                                borderRadius: 0.5,
                                minHeight: 44,
                                px: 1.5,
                                py: 1,
                                cursor: clickable ? 'pointer' : 'default',
                                '&:hover': clickable ? { opacity: 0.9 } : undefined,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 65 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                  {unit.unitNumber || '?'}
                                </Typography>
                                {bedroomType && (
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                                    ({bedroomType})
                                  </Typography>
                                )}
                              </Box>
                              {isSold ? (
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#f44336' }}>
                                  SOLD
                                </Typography>
                              ) : isNotReleased ? (
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>-</Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.2 }}>
                                  {(() => {
                                    const revealed = resolveRevealed(unit);
                                    const priceCell =
                                      formatRevealedDollar(revealed?.price)
                                      ?? formatMaskedPrice(unit.priceDisplay);
                                    const psfCell = (() => {
                                      const revealedPsf = formatRevealedDollar(revealed?.psf);
                                      if (revealedPsf) return `${revealedPsf}/sqft`;
                                      return unit.psfDisplay
                                        ? `${formatMaskedPrice(unit.psfDisplay)}/sqft`
                                        : '-';
                                    })();
                                    return (
                                      <>
                                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                          {priceCell}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                                          {psfCell}
                                        </Typography>
                                      </>
                                    );
                                  })()}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
            No stack analysis data available.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StackAnalysisDialog;
